import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/trigger-catalog-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/v1/triggers',
        destPath: '/v1/triggers',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/v1/triggers',
        destPath: '/v1/triggers',
      },
      {
        srcPath: '/automations/triggercatalog/v1/trigger',
        destPath: '/v1/trigger',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/trigger-catalog-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_automations_trigger-catalog';

/**
 * Creates a trigger for the specified app. Once created, users can [create automations](https://support.wix.com/en/wix-automations-7067897) that run when this trigger occurs.
 *
 * Learn more about [triggers](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/about-triggers).
 *
 * > **Note:** You can only create triggers for [apps you own or collaborate on](https://dev.wix.com/docs/build-apps/develop-your-app/app-dashboard-setup/manage-collaborators).
 */
export function createTrigger(payload: object): RequestOptionsFactory<any> {
  function __createTrigger({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'trigger.createdDate' },
          { path: 'trigger.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.CreateTrigger',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'trigger.createdDate' },
              { path: 'trigger.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createTrigger;
}

/**
 * Updates the specified trigger for the specified app.
 *
 * After the trigger is updated, it only contains the values specified in the request. Earlier values are replaced.
 */
export function updateTrigger(payload: object): RequestOptionsFactory<any> {
  function __updateTrigger({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'trigger.createdDate' },
          { path: 'trigger.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'PATCH' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.UpdateTrigger',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/trigger/{trigger.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'updatedTrigger.createdDate' },
              { path: 'updatedTrigger.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateTrigger;
}

/**
 * Deletes a trigger from the specified app.
 *
 * > **Note**: [Automations that have the deleted trigger](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger) no longer run.
 */
export function deleteTriggerByAppIdAndKey(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteTriggerByAppIdAndKey({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'DELETE' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.DeleteTriggerByAppIdAndKey',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/trigger',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteTriggerByAppIdAndKey;
}

/** Retrieves the specified trigger. */
export function getTriggerByAppIdAndKey(
  payload: object
): RequestOptionsFactory<any> {
  function __getTriggerByAppIdAndKey({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'GET' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.GetTriggerByAppIdAndKey',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers/apps/{appId}/keys/{triggerKey}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'trigger.createdDate' },
              { path: 'trigger.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTriggerByAppIdAndKey;
}

/**
 * Retrieves a list of triggers, given the provided paging, filtering, and sorting.
 *
 * To query all triggers by a particular app, specify its `appId` in the query's `filter` object.
 *
 *
 * Query Triggers runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * To learn about working with query methods, see:
 * - [API Query Language](https://dev.wix.com/docs/api-reference/articles/getting-started/api-query-language)
 * - [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging)
 * - [Field projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection)
 */
export function queryTriggers(payload: object): RequestOptionsFactory<any> {
  function __queryTriggers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.QueryTriggers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.createdDate' },
              { path: 'results.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryTriggers;
}

/** Returns triggers that are installed on the site and have sufficient maturity. */
export function resolveTriggers(payload: object): RequestOptionsFactory<any> {
  function __resolveTriggers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.ResolveTriggers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers/resolve',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.createdDate' },
              { path: 'results.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __resolveTriggers;
}

/**
 * Retrieves a trigger's payload schema with the user's [filter field](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/filter-fields) selections applied.
 *
 * Learn more about [the trigger payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).
 */
export function getTriggerDynamicSchema(
  payload: object
): RequestOptionsFactory<any> {
  function __getTriggerDynamicSchema({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.GetTriggerDynamicSchema',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers/dynamic_schema',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getTriggerDynamicSchema;
}

/** Returns trigger identities schema */
export function getIdentitiesSchema(
  payload: object
): RequestOptionsFactory<any> {
  function __getIdentitiesSchema({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.triggercatalog.v1.trigger',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.triggercatalog.v1.TriggerCatalogService.GetIdentitiesSchema',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsTriggercatalogV1TriggerCatalogServiceUrl({
        protoPath: '/v1/triggers/identities_schema',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getIdentitiesSchema;
}
